// SPDX-License-Identifier: AGPL-3.0-or-later

import {PricingCard} from '@app/features/app/components/dialogs/components/PricingCard';
import gridStyles from '@app/features/app/components/dialogs/components/PricingGrid.module.css';
import {PurchaseDisclaimer} from '@app/features/app/components/dialogs/components/PurchaseDisclaimer';
import {PricingContextPanel} from '@app/features/app/components/dialogs/components/plutonium/PricingContextPanel';
import styles from '@app/features/app/components/dialogs/components/plutonium/PricingSection.module.css';
import {PurchaseDisabledWrapper} from '@app/features/app/components/dialogs/components/plutonium/PurchaseDisabledWrapper';
import {ToggleButton} from '@app/features/app/components/dialogs/components/ToggleButton';
import {PREMIUM_PRODUCT_FULL_NAME, PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import type {PricingMode} from '@app/features/premium/utils/PricingUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {ArrowDownIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface PricingSectionProps {
	isGiftMode: boolean;
	setIsGiftMode: (value: boolean) => void;
	countryCode: string | null;
	pricingMode: PricingMode;
	setPricingMode: (value: PricingMode) => void;
	hasPricingChoice: boolean;
	localizedCurrency: string | null;
	baseCurrency: string | null;
	monthlyPrice: string;
	yearlyPrice: string;
	giftMonthlyPrice: string;
	giftYearlyPrice: string;
	loadingCheckout: boolean;
	handleSelectPlan: (plan: 'monthly' | 'yearly' | 'gift_1_month' | 'gift_1_year') => void;
	purchaseDisabled?: boolean;
	purchaseDisabledTooltip?: React.ReactNode;
}
export const PricingSection: React.FC<PricingSectionProps> = observer(
	({
		isGiftMode,
		setIsGiftMode,
		countryCode,
		pricingMode,
		setPricingMode,
		hasPricingChoice,
		localizedCurrency,
		baseCurrency,
		monthlyPrice,
		yearlyPrice,
		giftMonthlyPrice,
		giftYearlyPrice,
		loadingCheckout,
		handleSelectPlan,
		purchaseDisabled = false,
		purchaseDisabledTooltip,
	}) => {
		const {t} = useLingui();
		const tooltipText: React.ReactNode =
			purchaseDisabledTooltip ?? t`Claim your account to purchase ${PREMIUM_PRODUCT_FULL_NAME}.`;
		return (
			<section className={styles.section}>
				<div className={styles.toggleContainer} role="group" aria-label={t`Purchase mode`}>
					<ToggleButton active={!isGiftMode} onClick={() => setIsGiftMode(false)} label={t`For Me`} />
					<ToggleButton active={isGiftMode} onClick={() => setIsGiftMode(true)} label={t`As a Gift`} />
				</div>
				<PricingContextPanel
					countryCode={countryCode}
					pricingMode={pricingMode}
					setPricingMode={setPricingMode}
					hasPricingChoice={hasPricingChoice}
					localizedCurrency={localizedCurrency}
					baseCurrency={baseCurrency}
					isGiftMode={isGiftMode}
				/>
				<div className={gridStyles.gridWrapper}>
					<div className={gridStyles.gridTwoColumns}>
						{!isGiftMode ? (
							<>
								<PurchaseDisabledWrapper disabled={purchaseDisabled} tooltipText={tooltipText}>
									<PricingCard
										title={t`Monthly`}
										price={monthlyPrice}
										period={t`per month`}
										onSelect={() => handleSelectPlan('monthly')}
										isLoading={loadingCheckout}
										disabled={purchaseDisabled}
									/>
								</PurchaseDisabledWrapper>
								<PurchaseDisabledWrapper disabled={purchaseDisabled} tooltipText={tooltipText}>
									<PricingCard
										title={t`Yearly`}
										price={yearlyPrice}
										period={t`per year`}
										badge={t`Save 17%`}
										isPopular
										onSelect={() => handleSelectPlan('yearly')}
										buttonText={t`Upgrade Now`}
										isLoading={loadingCheckout}
										disabled={purchaseDisabled}
									/>
								</PurchaseDisabledWrapper>
							</>
						) : (
							<>
								<PurchaseDisabledWrapper disabled={purchaseDisabled} tooltipText={tooltipText}>
									<PricingCard
										title={t`1 Year Gift`}
										price={giftYearlyPrice}
										period={t`one-time purchase`}
										badge={t`Save 17%`}
										onSelect={() => handleSelectPlan('gift_1_year')}
										buttonText={t`Buy Gift`}
										isLoading={loadingCheckout}
										disabled={purchaseDisabled}
									/>
								</PurchaseDisabledWrapper>
								<PurchaseDisabledWrapper disabled={purchaseDisabled} tooltipText={tooltipText}>
									<PricingCard
										title={t`1 Month Gift`}
										price={giftMonthlyPrice}
										period={t`one-time purchase`}
										isPopular
										onSelect={() => handleSelectPlan('gift_1_month')}
										buttonText={t`Buy Gift`}
										isLoading={loadingCheckout}
										disabled={purchaseDisabled}
									/>
								</PurchaseDisabledWrapper>
							</>
						)}
					</div>
				</div>
				<div className={styles.footerContainer}>
					<PurchaseDisclaimer />
					<div className={styles.scrollPromptContainer}>
						<p className={styles.scrollPromptText}>
							<Trans>Scroll down to view all the sweet perks you get with {PREMIUM_PRODUCT_NAME}</Trans>
						</p>
						<ArrowDownIcon className={styles.scrollPromptIcon} weight="bold" />
					</div>
				</div>
			</section>
		);
	},
);
